import React, { useMemo, useState } from 'react';
import { CodeBlock } from './CodeBlock';
import ReactMarkdown from 'react-markdown';
import { ImageBlock, VideoBlock } from './MediaBlocks';
import { ChromebookCaseVisual } from './ChromebookCaseVisual';

interface StepRendererProps {
  title?: string | null;
  stageTag?: string | null;
  stepKind?: string | null;
  content?: any;
  titleClassName?: string;
  hideHeader?: boolean;
  mediaSessionKey?: string;
  preserveVideoPosition?: boolean;
}

const normalizeComparable = (value: any): string => {
  if (typeof value !== 'string') return '';
  return value
    .replace(/\r/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[“”]/g, '"')
    .replace(/[’]/g, "'");
};

const renderFallback = (content: any) => {
  if (content == null) return null;
  if (typeof content === 'string') {
    return <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">{content}</p>;
  }
  return null;
};

export const StepRenderer: React.FC<StepRendererProps> = ({
  title,
  stageTag,
  stepKind,
  content,
  titleClassName,
  hideHeader = false,
  mediaSessionKey,
  preserveVideoPosition = false,
}) => {
  const blocks: any[] = Array.isArray(content?.blocks) ? content.blocks : [];
  const hasBlocks = blocks.length > 0;
  const text = typeof content?.text === 'string' ? content.text : null;
  const promptMd = typeof content?.prompt_md === 'string' ? content.prompt_md : null;
  const sourceItem = content?.source_item && typeof content.source_item === 'object'
    ? content.source_item
    : (content?.sourceItem && typeof content.sourceItem === 'object' ? content.sourceItem : null);
  const sourceType = String(sourceItem?.type || '').trim().toLowerCase();
  const orderingItems = sourceType === 'ordering' && Array.isArray(sourceItem?.items)
    ? sourceItem.items.map((item: any) => String(item ?? '').trim()).filter((item: string) => item.length > 0)
    : [];
  const orderingQuestion = sourceType === 'ordering' ? String(sourceItem?.question || '').trim() : '';
  const renderStructuredOrdering = sourceType === 'ordering' && orderingItems.length > 0;
  const dragDropTokens = (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks')
    ? (Array.isArray(sourceItem?.tokens)
      ? sourceItem.tokens
      : (Array.isArray(sourceItem?.options)
        ? sourceItem.options
        : (Array.isArray(sourceItem?.token_bank) ? sourceItem.token_bank : [])))
      .map((item: any) => String(item ?? '').trim())
      .filter((item: string) => item.length > 0)
    : [];
  const dragDropQuestion = (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks')
    ? String(sourceItem?.question || '').trim()
    : '';
  const dragDropTarget = (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks')
    ? String(sourceItem?.target || sourceItem?.code || sourceItem?.template || '').trim()
    : '';
  const dragDropSegments = dragDropTarget ? dragDropTarget.split('____') : [];
  const dragDropBlankCount = Math.max(dragDropSegments.length - 1, 0);
  const renderStructuredDragDrop = (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks')
    && (dragDropTarget.length > 0 || dragDropTokens.length > 0);
  const [expanded, setExpanded] = useState(false);
  const textLines = useMemo(() => (text ? text.split('\n') : []), [text]);
  const isLongText = textLines.length > 12;
  const visibleText = isLongText && !expanded ? textLines.slice(0, 12).join('\n') : text;
  const blocksForRender = useMemo(() => {
    if (!Array.isArray(blocks) || blocks.length === 0) return blocks;
    const first = blocks[0];
    if (!first || typeof first !== 'object') return blocks;
    if (first.type !== 'text') return blocks;

    const firstText = normalizeComparable(first.text);
    if (!firstText) return blocks;

    const titleNorm = normalizeComparable(title);
    const promptNorm = normalizeComparable(promptMd);

    // If the first text block is just repeating the title/prompt, drop it.
    if ((titleNorm && firstText === titleNorm) || (promptNorm && firstText === promptNorm)) {
      return blocks.slice(1);
    }
    return blocks;
  }, [blocks, title, promptMd]);
  const markdownComponents = {
    p({ children }: any) {
      return <p className="my-3 leading-relaxed">{children}</p>;
    },
    h1({ children }: any) {
      return <h1 className="mt-5 mb-3 text-3xl font-semibold text-gray-900">{children}</h1>;
    },
    h2({ children }: any) {
      return <h2 className="mt-5 mb-3 text-2xl font-semibold text-gray-900">{children}</h2>;
    },
    h3({ children }: any) {
      return <h3 className="mt-4 mb-2 text-xl font-semibold text-gray-900">{children}</h3>;
    },
    ul({ children }: any) {
      return <ul className="my-3 list-disc pl-6 space-y-1">{children}</ul>;
    },
    ol({ children }: any) {
      return <ol className="my-3 list-decimal pl-6 space-y-1">{children}</ol>;
    },
    li({ children }: any) {
      return <li className="leading-relaxed">{children}</li>;
    },
    strong({ children }: any) {
      return <strong className="font-semibold text-gray-900">{children}</strong>;
    },
    em({ children }: any) {
      return <em className="italic">{children}</em>;
    },
    blockquote({ children }: any) {
      return <blockquote className="my-3 border-l-4 border-gray-300 pl-3 text-gray-700">{children}</blockquote>;
    },
    a({ href, children }: any) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="mr-5 mb-2 inline-flex max-w-full items-baseline break-words text-blue-600 underline"
        >
          {children}
        </a>
      );
    },
    code({ inline, className, children, ...props }: any) {
      return !inline ? (
        <pre className="bg-gray-900 text-green-200 p-3 rounded text-sm overflow-x-auto">
          <code className={className} {...props}>{children}</code>
        </pre>
      ) : (
        <code className="bg-gray-100 px-1 rounded">
          {children}
        </code>
      );
    },
  };

  return (
    <section className="space-y-6">
      {typeof content?.chromebook_condition === 'string' && <ChromebookCaseVisual condition={content.chromebook_condition} />}
      {!hideHeader && (
        <div>
          {stageTag && (
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{stageTag}</p>
          )}
          <h1 className={`mt-2 font-semibold text-gray-900 ${titleClassName || 'text-3xl md:text-4xl'}`}>
            {title || 'Current Step'}
          </h1>
          {stepKind && <p className="mt-2 text-sm text-gray-500">{stepKind}</p>}
        </div>
      )}

      {renderStructuredOrdering && (
        <div className="space-y-3">
          {orderingQuestion && (
            <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">{orderingQuestion}</p>
          )}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Items</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {orderingItems.map((item: string, idx: number) => (
                <span key={`${idx}:${item}`} className="inline-flex rounded border border-gray-300 bg-gray-50 px-2 py-1 text-sm text-gray-800">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {renderStructuredDragDrop && (
        <div className="space-y-3">
          {dragDropQuestion && (
            <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">{dragDropQuestion}</p>
          )}
          {dragDropTarget && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
              {dragDropSegments.map((segment: string, idx: number) => (
                <React.Fragment key={`drag-seg-${idx}`}>
                  <span className="whitespace-pre-wrap">{segment}</span>
                  {idx < dragDropBlankCount ? (
                    <span className="inline-flex mx-1 w-16 border-b-2 border-dashed border-gray-400 align-middle" />
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          )}
          {dragDropTokens.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Token bank</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {dragDropTokens.map((token: string, idx: number) => (
                  <span key={`${idx}:${token}`} className="inline-flex rounded border border-gray-300 bg-gray-50 px-2 py-1 text-sm text-gray-800">
                    {token}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!renderStructuredOrdering && !renderStructuredDragDrop && !hasBlocks && promptMd && (
        <div className="prose max-w-none">
          <ReactMarkdown components={markdownComponents}>
            {promptMd}
          </ReactMarkdown>
        </div>
      )}

      {!hasBlocks && !promptMd && text && (
        <div>
          <div className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
            {visibleText}
          </div>
          {isLongText && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}

      {hasBlocks && (
        <div className="space-y-5">
          {blocksForRender.map((block, index) => {
            if (!block || typeof block !== 'object') {
              return (
                <div key={`block-${index}`} className="text-gray-700 text-lg">
                  {String(block)}
                </div>
              );
            }
            if (block.type === 'text') {
              return (
                <div key={`block-${index}`} className="prose prose-lg max-w-none text-gray-700">
                  <ReactMarkdown components={markdownComponents}>
                    {String(block.text || '')}
                  </ReactMarkdown>
                </div>
              );
            }
            if (block.type === 'code') {
              return (
                <CodeBlock
                  key={`block-${index}`}
                  code={block.code || ''}
                  language={block.language}
                />
              );
            }
            if (block.type === 'link') {
              return (
                <div key={`block-${index}`}>
                  <a
                    href={block.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex max-w-full items-center gap-2 break-words text-blue-600 font-medium underline"
                  >
                    {block.label || block.url}
                  </a>
                </div>
              );
            }
            if (block.type === 'image') {
              return <ImageBlock key={`block-${index}`} block={block} />;
            }
            if (block.type === 'video') {
              return (
                <VideoBlock
                  key={`block-${index}`}
                  block={block}
                  sessionKey={mediaSessionKey ? `${mediaSessionKey}:${index}` : undefined}
                  preservePosition={preserveVideoPosition}
                />
              );
            }
            return (
              <div key={`block-${index}`} className="text-gray-700 text-lg">
                {renderFallback(block)}
              </div>
            );
          })}
        </div>
      )}

      {['helpful_example', 'small_steps', 'choice_menu'].map((key) => typeof content?.learning_support?.[key] === 'string' && (
        <details key={key} className="rounded-xl border border-purple-200 p-4">
          <summary className="cursor-pointer font-semibold text-purple-900">{key === 'helpful_example' ? 'Helpful example' : key === 'small_steps' ? 'One step at a time' : 'Optional choices — check with your teacher'}</summary>
          <ReactMarkdown components={markdownComponents}>{content.learning_support[key]}</ReactMarkdown>
        </details>
      ))}
      {!hasBlocks && !promptMd && !text && renderFallback(content)}
    </section>
  );
};
